import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import { HerbPrescription } from '../types';
import axios from 'axios';
import apiClient, { API_BASE_URL } from '../services/apiClient';
import './PrescriptionDetail.css';

const PrescriptionDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [prescription, setPrescription] = useState<HerbPrescription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const originState = (location.state as { from?: string } | null) ?? null;
  const fallbackRoute = originState?.from || '/app/chat';

  useEffect(() => {
    const fetchPrescription = async () => {
      if (!id) {
        setError('未提供处方ID');
        setLoading(false);
        return;
      }
      const normalizedId = id.trim();
      if (!normalizedId) {
        setError('未提供处方ID');
        setLoading(false);
        return;
      }

      const primaryPath = `/herb-library/prescriptions/${normalizedId}`;
      const primaryAbsolute = `${API_BASE_URL}${primaryPath}`;
      const searchParams = new URLSearchParams(location.search);
      const sourceParam = searchParams.get('source');
      const fallbackUrl = sourceParam ? decodeURIComponent(sourceParam) : null;

      try {
        const response = await apiClient.get<HerbPrescription>(primaryPath);
        setPrescription(response.data);
        setError(null);
      } catch (err) {
        console.error('获取处方失败', err);

        if (fallbackUrl && fallbackUrl !== primaryAbsolute) {
          try {
            const isSameOrigin = fallbackUrl.startsWith(API_BASE_URL);
            if (isSameOrigin) {
              const relative = fallbackUrl.slice(API_BASE_URL.length) || '/';
              const normalizedFallback = relative.startsWith('/') ? relative : `/${relative}`;
              const fallbackResponse = await apiClient.get<HerbPrescription>(normalizedFallback);
              setPrescription(fallbackResponse.data);
            } else {
              const fallbackResponse = await axios.get<HerbPrescription>(fallbackUrl);
              setPrescription(fallbackResponse.data);
            }
            setError(null);
            return;
          } catch (fallbackErr) {
            console.error('备用链接获取处方失败', fallbackErr);
          }
        }

        setError('未能加载处方内容，请稍后再试。');
      } finally {
        setLoading(false);
      }
    };

    fetchPrescription();
  }, [id, location.search]);

  const handleBack = () => {
    if (originState?.from) {
      navigate(originState.from);
      return;
    }

    if (typeof window !== 'undefined' && window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate(fallbackRoute);
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="prescription-page-wrapper">
      <header className="prescription-header">
        <div>
          <h2>{prescription?.title ?? 'AI定制处方'}</h2>
          {prescription?.created_at && (
            <p className="prescription-meta">
              生成时间：{new Date(prescription.created_at).toLocaleString()}
            </p>
          )}
        </div>
        <div className="prescription-actions">
          <button type="button" onClick={handleBack} className="ghost-button">
            返回
          </button>
          <button type="button" onClick={handlePrint} className="primary-button">
            打印
          </button>
        </div>
      </header>

      {loading ? (
        <div className="prescription-loading">正在加载处方内容…</div>
      ) : error ? (
        <div className="prescription-error">{error}</div>
      ) : prescription ? (
        <div className="prescription-content">
          <section className="prescription-summary">
            {prescription.diagnosis && (
              <div className="summary-card">
                <h4>辨证结论</h4>
                <p>{prescription.diagnosis}</p>
              </div>
            )}
            {prescription.herbs.length > 0 && (
              <div className="summary-card">
                <h4>药材组成</h4>
                <table>
                  <thead>
                    <tr>
                      <th>药材</th>
                      <th>剂量</th>
                      <th>功效说明</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prescription.herbs.map((herb) => (
                      <tr key={`${herb.name}-${herb.dosage}`.replace(/\s+/g, '')}>
                        <td>{herb.name}</td>
                        <td>{herb.dosage || '—'}</td>
                        <td>{herb.reason || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <article className="prescription-markdown">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeSanitize]}
            >
              {prescription.prescription_text}
            </ReactMarkdown>
          </article>
        </div>
      ) : (
        <div className="prescription-error">找不到处方内容。</div>
      )}
    </div>
  );
};

export default PrescriptionDetail;
